import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { trackEvent } from "@/utils/tagManagerUtils";
import { ArrowRight, Sparkles, Brain, Cpu, MessageSquare } from "lucide-react";

const AiGoogleAds = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackEvent("page_view", { path: "/ai-google-ads" });
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>AI Google Ads Platform | Create Campaigns with AI | Upfixe</title>
        <meta
          name="description"
          content="Leverage the power of AI for Google Ads. Upfixe uses advanced artificial intelligence to write ad copy, find keywords, and structure campaigns perfectly."
        />
        <meta
          name="keywords"
          content="ai google ads, artificial intelligence google ads, ai ads generator, chatgpt google ads, claude google ads"
        />
        <link rel="canonical" href="https://upfixe.com/ai-google-ads" />
      </Helmet>

      <Header />

      <main className="flex-grow pt-24 pb-16">
        <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary font-medium text-sm mb-6">
            <Sparkles className="h-4 w-4" /> Next-Generation AI Ads
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6">
            The Future is <span className="text-primary">AI Google Ads</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
            Don't just automate—innovate. Upfixe is the first native AI Google Ads platform that 
            understands your business context and generates agency-quality campaigns from a single prompt.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button asChild size="lg" className="text-lg px-8 py-6 rounded-full">
              <a href="https://app.upfixe.com/signup">
                Try AI Campaign Builder <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <Brain className="h-10 w-10 text-primary mb-5" />
              <h3 className="text-xl font-bold mb-3">Context-Aware AI</h3>
              <p className="text-gray-600">
                Unlike simple keyword scrapers, our AI analyzes your entire landing page to understand your value proposition, 
                target audience, and unique selling points before writing a single word of ad copy.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <MessageSquare className="h-10 w-10 text-primary mb-5" />
              <h3 className="text-xl font-bold mb-3">Conversational Interface</h3>
              <p className="text-gray-600">
                Through our MCP integration, you can manage your Google Ads directly by chatting with Claude or ChatGPT. 
                Just ask the AI to "create a campaign for our new spring collection" and watch it work.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <Cpu className="h-10 w-10 text-primary mb-5" />
              <h3 className="text-xl font-bold mb-3">Perfect Campaign Structure</h3>
              <p className="text-gray-600">
                AI doesn't make structural mistakes. Upfixe guarantees best-practice campaign architecture, 
                grouping keywords logically and applying the correct match types and negative keyword lists automatically.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-primary text-white py-20 px-4 sm:px-6 lg:px-8 mt-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to let AI handle your Google Ads?</h2>
          <p className="text-xl opacity-90 max-w-2xl mx-auto mb-10">
            Join the hundreds of marketers and founders using Upfixe to generate high-converting campaigns in minutes.
          </p>
          <Button asChild size="lg" variant="secondary" className="text-lg px-8 py-6 rounded-full text-primary font-bold">
            <a href="https://app.upfixe.com/signup">Start Your 7-Day Free Trial</a>
          </Button>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AiGoogleAds;
